import { type StrikePlate, StrikePlateShapeType } from 'metalworks-domain'

import {
  AttributeListSelector,
  AttributeSwitchSelector,
  AttributeToggleSelector,
  DistanceDimensionInput,
} from '@salsita-ai/platform-project-sdk/react'
import { EntityPanel, PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import {
  FinishDirectionProperty,
  FinishProperty,
  HandingProperty,
  MaterialSwatchProperty,
  NonStandardMaterialWarningWrapper,
  PowderCoatingProperty,
  PowderCoatingWarningWrapper,
  ThicknessProperty,
} from './shared-property-page-components'

interface Props {
  id: StrikePlate['id']
  shapeType: StrikePlateShapeType
  standard: boolean
  hasFinishDirection: boolean
  powderCoating: boolean
  hasBend: boolean
}

const mainCornerRadiusFixableViolations = [
  'tongueOffsetStartTooSmallForCornerRadii',
  'tongueOffsetEndTooSmallForCornerRadii',
  'jointCornerRadiusTooLargeForCornerRadii',
  'tongueOffsetStart.distanceDimension.tooSmall',
  'tongueOffsetEnd.distanceDimension.tooSmall',
]

const tongueOffsetStartFixableViolations = [
  'tongueOffsetStart.distanceDimension.tooSmall',
  'cornerRadiusTooLarge',
  'tongueOffsetEndTooSmallForCornerRadii',
  'jointCornerRadiusTooLargeForCornerRadii',
]

const tongueOffsetEndFixableViolations = [
  'tongueOffsetEnd.distanceDimension.tooSmall',
  'cornerRadiusTooLarge',
  'tongueOffsetStartTooSmallForCornerRadii',
  'jointCornerRadiusTooLargeForCornerRadii',
]

const jointCornerRadiusFixableViolations = [
  'jointCornerRadius.distanceDimension.tooLarge',
  'cornerRadiusTooLarge',
  'tongueOffsetStartTooSmallForCornerRadii',
  'tongueOffsetEndTooSmallForCornerRadii',
  'tongueOffsetStart.distanceDimension.tooSmall',
  'tongueOffsetEnd.distanceDimension.tooSmall',
]

export const StrikePlatePropertyPage = (props: Props) => {
  const { id, standard, hasFinishDirection, powderCoating, hasBend, shapeType } = props

  return (
    <EntityPanel.Layout
      additionalAction={null}
      header={
        <EntityPanel.Header
          title="Strike Plate"
          description="Customise your strike plate. You can change the length, width, radiuses, thickness and bend settings of your plate and select your preferred material."
          previewImage={undefined}
          primaryButton={null}
          secondaryButton={null}
        />
      }
    >
      <PropertyContainer.Group spacing="large">
        <MaterialSwatchProperty id={id} />
        <PowderCoatingProperty id={id} />
        <PowderCoatingWarningWrapper powderCoating={powderCoating} />
        <FinishProperty id={id} />
        {hasFinishDirection && <FinishDirectionProperty id={id} />}
        <HandingProperty id={id} />

        <PropertyContainer.Item title="Plate Type">
          <AttributeToggleSelector entityId={id} instanceName="shapeType" />
        </PropertyContainer.Item>

        {shapeType === StrikePlateShapeType.TStrike && (
          <PropertyContainer.Item>
            <AttributeSwitchSelector entityId={id} instanceName="hasBend" title="Add Bend" />
          </PropertyContainer.Item>
        )}

        <PropertyContainer.Item title="Bend Position">
          <AttributeToggleSelector entityId={id} instanceName="tonguePosition" />
        </PropertyContainer.Item>

        {hasBend && (
          <PropertyContainer.Item title="Bend Type">
            <AttributeListSelector entityId={id} instanceName="bendType" />
          </PropertyContainer.Item>
        )}

        <ThicknessProperty id={id} />
        <NonStandardMaterialWarningWrapper standard={standard} />

        {shapeType === StrikePlateShapeType.TStrike ? (
          <PropertyContainer.Item title="Main Width">
            <DistanceDimensionInput
              entityId={id}
              instanceName="tStrikeMainWidth"
              fixableViolations={[
                'tongueHorizontalLength.distanceDimension.tooLarge',
                'horizontalLength.distanceDimension.tooSmall',
                'cornerRadius.genericDimension.tooLarge',
                'mainCornerRadius.distanceDimension.tooLarge',
              ]}
            />
          </PropertyContainer.Item>
        ) : (
          <PropertyContainer.Item title="Width">
            <DistanceDimensionInput
              entityId={id}
              instanceName="horizontalLength"
              fixableViolations={[
                'tongueHorizontalLength.distanceDimension.tooLarge',
                'cornerRadius.genericDimension.tooLarge',
                'mainCornerRadius.distanceDimension.tooLarge',
                'tStrikeMainWidth.distanceDimension.tooSmall',
              ]}
            />
          </PropertyContainer.Item>
        )}
        <PropertyContainer.Item title="Height">
          <DistanceDimensionInput entityId={id} instanceName="verticalLength" />
        </PropertyContainer.Item>

        <PropertyContainer.Item title="Corner Radius">
          <DistanceDimensionInput
            entityId={id}
            instanceName="mainCornerRadius"
            fixableViolations={mainCornerRadiusFixableViolations}
          />
        </PropertyContainer.Item>

        {shapeType === StrikePlateShapeType.TStrike && (
          <>
            <PropertyContainer.Item title="Tongue Width">
              <DistanceDimensionInput
                entityId={id}
                instanceName="tongueHorizontalLength"
                fixableViolations={[
                  'tongueHorizontalLength.distanceDimension.tooLarge',
                  'horizontalLength.distanceDimension.tooSmall',
                ]}
              />
            </PropertyContainer.Item>

            <PropertyContainer.Item title="Tongue Bottom Offset">
              <DistanceDimensionInput
                entityId={id}
                instanceName="tongueOffsetStart"
                fixableViolations={tongueOffsetStartFixableViolations}
              />
            </PropertyContainer.Item>
            <PropertyContainer.Item title="Tongue Top Offset">
              <DistanceDimensionInput
                entityId={id}
                instanceName="tongueOffsetEnd"
                fixableViolations={tongueOffsetEndFixableViolations}
              />
            </PropertyContainer.Item>

            <PropertyContainer.Item title="Joint Corner Radius">
              <DistanceDimensionInput
                entityId={id}
                instanceName="jointCornerRadius"
                fixableViolations={jointCornerRadiusFixableViolations}
              />
            </PropertyContainer.Item>
          </>
        )}

        <PropertyContainer.Item title="Bend Corner Radius">
          <DistanceDimensionInput entityId={id} instanceName="tongueCornerRadius" />
        </PropertyContainer.Item>
      </PropertyContainer.Group>
    </EntityPanel.Layout>
  )
}
